import {
  ChartData,
  ReportContext,
  SingeValueReport,
  SingleValueReportContext,
} from '@bm-js/atlas-analytics'
import { fetcher } from '../utils/fetcher'
import { useToken, useWebsite } from '../utils/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import AnalyticsSingleValue from './AnalyticsSingleValue'
import AnalyticsGraph from './AnalyticsGraph'
import { graphComponentMap } from '../utils/analytics.utils'

type SingleValueProps = {
  type: 'singleValue'
  context: SingleValueReportContext
}

type GraphProps = {
  type: 'graph'
  context: ReportContext
}

type Props = (SingleValueProps | GraphProps) & {
  dateQuery: string
}

const AnalyticsDisplay = ({ context, dateQuery, type }: Props) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<SingeValueReport | ChartData | null>(null)
  const { atlasWebsite } = useWebsite()
  const token = useToken()

  const analyticsQuery = useMemo(() => {
    if (type === 'singleValue') {
      return `?singleValueContext=${context}&${dateQuery}`
    }
    return `?context=${context}&${dateQuery}`
  }, [context, type, dateQuery])

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true)
    if (!atlasWebsite?.atlasAnalyticsReporterId) return
    const { data, err } = await fetcher<SingeValueReport | ChartData>({
      path: `analytics/${atlasWebsite._id}${analyticsQuery}`,
      auth: token,
    })

    if (err || !data) return

    setData(data)
    setLoading(false)
  }, [analyticsQuery, atlasWebsite, token])

  useEffect(() => {
    // eslint-disable-next-line
    fetchAnalyticsData()
  }, [fetchAnalyticsData])

  if (type === 'singleValue') {
    return (
      <AnalyticsSingleValue
        context={context}
        data={data as SingeValueReport | null}
        loading={loading}
      />
    )
  }

  return (
    <AnalyticsGraph
      context={context}
      data={data as ChartData | null}
      loading={loading}
      ChartComponent={graphComponentMap[context]}
    />
  )
}

export default AnalyticsDisplay
